import { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import {
  recommendationFilterSettingsAtom,
  PREFECTURE_OPTIONS,
  MOOD_CATEGORY_OPTIONS,
  GENRE_OPTIONS,
  ALL_PREFECTURE_VALUES,
  type RecommendationFilterSettings
} from "@/atoms/recommendationFilterAtom";
import { FilterToggleButton } from "@/common/FilterToggleButton";

type RecommendationFilterDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const RecommendationFilterDrawer = ({
  open,
  onOpenChange
}: RecommendationFilterDrawerProps) => {
  const [filterSettings, setFilterSettings] = useAtom(recommendationFilterSettingsAtom);

  // ローカル状態でフィルタ設定を管理（確定まで実際のatomは更新しない）
  const [localSettings, setLocalSettings] = useState<RecommendationFilterSettings>(filterSettings);

  // filterSettingsが変更された時（LocalStorageからの復元も含む）にlocalSettingsを同期
  useEffect(() => {
    setLocalSettings(filterSettings);
  }, [filterSettings]);

  // Drawerが開かれた時にローカル設定を初期化
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setLocalSettings(filterSettings);
    }
    onOpenChange(newOpen);
  };

  // 都道府県の選択/解除
  const handlePrefectureChange = (prefecture: string, checked: boolean) => {
    setLocalSettings((prev) => ({
      ...prev,
      address_pref: checked
        ? [...prev.address_pref, prefecture]
        : prev.address_pref.filter((p) => p !== prefecture)
    }));
  };

  // 都道府県全選択/全解除
  const handleAllPrefecturesToggle = () => {
    const isAllSelected = ALL_PREFECTURE_VALUES.every((value) =>
      localSettings.address_pref.includes(value)
    );

    setLocalSettings((prev) => ({
      ...prev,
      address_pref: isAllSelected ? [] : [...ALL_PREFECTURE_VALUES]
    }));
  };

  // 相互排他選択の汎用ハンドラー
  const handleMutualExclusiveSelection = (key: "mood_category" | "genre", value: string) => {
    const otherKey = key === "mood_category" ? "genre" : "mood_category";
    setLocalSettings((prev) => ({
      ...prev,
      [key]: prev[key] === value ? null : value,
      [otherKey]: null // 他方をクリア
    }));
  };

  // Mood Categoryの選択/解除（genreと相互排他）
  const handleMoodCategoryChange = (moodCategory: string) => {
    handleMutualExclusiveSelection("mood_category", moodCategory);
  };

  // ジャンルの選択/解除（mood_categoryと相互排他）
  const handleGenreChange = (genreLabel: string) => {
    handleMutualExclusiveSelection("genre", genreLabel);
  };

  // 確定ボタン
  const handleConfirm = () => {
    setFilterSettings(localSettings);
    onOpenChange(false);
  };

  // リセットボタン
  const handleReset = () => {
    const resetSettings: RecommendationFilterSettings = {
      address_pref: [],
      mood_category: null,
      genre: null
    };
    setLocalSettings(resetSettings);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent className="bg-gray-50">
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader>
            <DrawerTitle>AIのカスタマイズ</DrawerTitle>
            <DrawerDescription>あなたの好みに合わせて推薦を調整できます</DrawerDescription>
          </DrawerHeader>

          <div className="max-h-[60vh] space-y-6 overflow-y-auto p-4 pb-0">
            {/* 都道府県選択 */}
            <div className="space-y-3 rounded-lg bg-white p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">都道府県</h3>
                <button
                  onClick={handleAllPrefecturesToggle}
                  className="text-xs text-gray-400 transition-colors hover:text-gray-600"
                >
                  全て
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {PREFECTURE_OPTIONS.map((option) => {
                  const isSelected = localSettings.address_pref.includes(option.value);
                  return (
                    <FilterToggleButton
                      key={option.value}
                      label={option.label}
                      isSelected={isSelected}
                      onClick={() => handlePrefectureChange(option.value, !isSelected)}
                    />
                  );
                })}
              </div>
            </div>

            {/* ジャンル・気分選択 */}
            <div className="space-y-4 rounded-lg bg-white p-4">
              {/* Mood Category セクション */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium">今日の気分でざっくり提案</h3>
                <div className="flex justify-center gap-3">
                  {MOOD_CATEGORY_OPTIONS.map((option) => {
                    const isSelected = localSettings.mood_category === option.value;
                    return (
                      <FilterToggleButton
                        key={option.value}
                        label={option.label}
                        isSelected={isSelected}
                        onClick={() => handleMoodCategoryChange(option.value)}
                        vertical={true}
                      />
                    );
                  })}
                </div>
              </div>

              {/* 点線区切り with "もしくは" */}
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-dashed border-gray-300"></div>
                </div>
                <div className="relative bg-white px-3 text-xs font-medium text-gray-500">
                  もしくは
                </div>
              </div>

              {/* Genre セクション */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium">ジャンルを絞って提案（1つのみ）</h3>
                <div className="space-y-3">
                  {GENRE_OPTIONS.map((category) => (
                    <div key={category.category}>
                      <h4 className="mb-2 text-xs font-medium text-gray-500">
                        {category.category}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {category.genres.map((genre) => {
                          const isSelected = localSettings.genre === genre.label;
                          return (
                            <FilterToggleButton
                              key={genre.label}
                              label={genre.label}
                              icon={genre.icon}
                              isSelected={isSelected}
                              onClick={() => handleGenreChange(genre.label)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <DrawerFooter className="pb-8 pt-6">
            <div className="flex gap-2">
              <Button onClick={handleReset} variant="outline" className="h-button-lg flex-1">
                リセット
              </Button>
              <Button onClick={handleConfirm} className="h-button-lg flex-1">
                確定
              </Button>
            </div>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
